// Importaciones de React, librerías y recursos.
import React from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2'; // Librería para crear alertas y modales atractivos.
import { useInView } from 'react-intersection-observer'; // Hook para detectar si un componente está en la pantalla.
import 'animate.css'; // Librería de animaciones CSS.

// Importación de las imágenes para cada tarjeta.
import imagen1 from '../img/Tixass.avif';
import imagen2 from '../img/cardd.avif';
import imagen3 from '../img/iso.avif';
import imagen4 from '../img/cardddd.avif';

/**
 * Componente ConsultoriaCard.
 * Renderiza una única tarjeta de servicio de consultoría.
 * @param {{consultoria: object, index: number}} props - Propiedades del componente.
 *   - `consultoria`: Objeto con los datos de la tarjeta (título, descripción, imagen).
 *   - `index`: Índice de la tarjeta, usado para escalonar las animaciones.
 */
const ConsultoriaCard = ({ consultoria, index }) => {
  // Hook para detectar cuando la tarjeta entra en el viewport.
  const [ref, inView] = useInView({
    triggerOnce: true, // La animación solo se dispara una vez.
    threshold: 0.1, // Se activa cuando al menos el 10% de la tarjeta es visible.
  });

  // Manejador para abrir el modal de SweetAlert2 con los detalles del servicio.
  const handleOpenModal = () => {
    Swal.fire({
      title: consultoria.title,
      text: consultoria.description,
      imageUrl: consultoria.imagen,
      imageWidth: 800,
      imageHeight: 300,
      showCloseButton: true,
      imageAlt: 'Imagen de consultoría',
      width: 1000,
      color: "black",
      focusConfirm: false,
      // Genera dinámicamente un enlace en el pie del modal.
      // Transforma el título (ej. "Servicio de CISO") en una URL amigable (ej. "servicio-de-ciso").
      footer: `<a href='./servicios/formulario-${consultoria.title.replace(/\s+/g, '-').toLowerCase()}' class='bg-blue-500 text-white py-2 px-4 rounded-md border-transparent hover:bg-blue-600 transition-colors duration-300 ml-4'>Más Información</a>`,
      showConfirmButton: false, // Oculta el botón de confirmación por defecto.
    });
  };

  return (
    // Contenedor de la tarjeta. Se le asigna la `ref` para la detección de visibilidad.
    // La clase de `animate.css` se aplica condicionalmente cuando `inView` es verdadero.
    <div
      ref={ref}
      className={`consultoria-card bg-white rounded-lg shadow-md p-4 transition-transform duration-500 ease-out ${inView ? 'animate__animated animate__zoomIn' : ''}`}
      style={{ animationDelay: `${index * 0.3}s` }} // Aplica un retraso a la animación para un efecto escalonado.
    >
      <div className="relative">
        {/* Título superpuesto en la imagen, que también abre el modal. */}
        <h2
          onClick={handleOpenModal}
          title='Ver detalles'
          className="cursor-pointer absolute inset-x-0 bottom-0 text-white bg-black bg-opacity-75 p-2 h-28 text-3xl"
        >
          {consultoria.title}
        </h2>
        {/* Imagen de la tarjeta, que también abre el modal. */}
        <img
          onClick={handleOpenModal}
          title='Ver detalles'
          src={consultoria.imagen}
          alt={consultoria.title}
          className="rounded-md mb-4 imagen-consultoria cursor-pointer"
        />
      </div>
      {/* Botón "Más Información" que enlaza a la página del formulario específico. */}
      <Link
        to={`./formulario-${consultoria.title.replace(/\s+/g, '-').toLowerCase()}`}
        className="bg-blue-500 text-white py-2 px-4 rounded-md border-transparent hover:bg-blue-600 transition-colors duration-300 mt-4 block text-center"
      >
        Más Información
      </Link>
    </div>
  );
};

/**
 * Componente Consultoria.
 * Es el contenedor principal que muestra la sección de "Consultoría Especializada".
 * Define los datos y renderiza una cuadrícula de componentes `ConsultoriaCard`.
 */
const Consultoria = () => {
  // Array con los datos de cada servicio de consultoría.
  const consultorias = [
    { id: 1, imagen: imagen1, title: 'Etiqueta Tisax', description: 'Trabajamos en conjunto para que tu empresa obtenga la etiqueta TISAX, nuestros servicios incluyen: levantamiento de información, diseño y documentación, generación de formatos para evidencias, entrenamiento al equipo participante, apoyo y acompañamiento en las auditorías internas y auditorias con el ente oficial.​' },
    { id: 2, imagen: imagen2, title: 'Servicio de CISO', description: 'Ofrecemos un servicio de consultoría en ciberseguridad que proporciona a su empresa un CISO (Chief Information Security Officer) altamente experimentado y líder durante un período específico y para proyectos particulares. Nuestros consultores cuentan con una amplia experiencia en diversos sectores, incluyendo el financiero, retail, manufactura y automotriz.' },
    { id: 3, imagen: imagen3, title: 'Certificación ISO27001', description: 'Apoyamos a tu empresa en la certificación ISO 27001 o, si prefieres, te ayudamos a implementar las mejores prácticas sin la necesidad de certificación. Nos encargamos de definir y documentar al menos 18 procesos clave para asegurar que estés alineado con los estándares de la ISO 27001.' },
    { id: 4, imagen: imagen4, title: 'Certificación ISO22301', description: 'Ofrecemos apoyo integral en la gestión de la continuidad del negocio, incluyendo la comprensión y aplicación de una estrategia de continuidad de negocios, la elaboración del Análisis de Impacto al Negocio (BIA), la definición de un plan de recuperación ante desastres (DR) y la generación de formatos para cada actividad. Además, proporcionamos entrenamiento al equipo participante y diseñamos ejercicios de gestión de crisis, entre otras actividades relacionadas para garantizar la resiliencia de su empresa frente a situaciones adversas.' }
  ];

  return (
    <div className="text-center p-20">
      <h2 className="text-4xl font-bold">CONSULTORÍA ESPECIALIZADA</h2>
      <div className='linea mt-8'></div>

      {/* Cuadrícula adaptable que organiza las tarjetas. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Itera sobre los datos y renderiza una `ConsultoriaCard` para cada uno. */}
        {consultorias.map((consultoria, index) => (
          <ConsultoriaCard key={consultoria.id} consultoria={consultoria} index={index} />
        ))}
      </div>
    </div>
  );
};

// Exporta el componente para ser usado en otras páginas.
export default Consultoria;
